import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../styles/Auth.css';

const AutoLogin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const hasAttemptedLogin = useRef(false);

  useEffect(() => {
    let isMounted = true;

    // Reset state on mount/token change
    hasAttemptedLogin.current = false;
    setError('');
    setIsLoading(true);

    const performAutoLogin = async () => {
      // Prevent multiple executions
      if (hasAttemptedLogin.current) {
        return;
      }
      hasAttemptedLogin.current = true;

      const token = searchParams.get('token');

      if (!token) {
        if (isMounted) {
          setError('Invalid login link. No token provided.');
          setIsLoading(false);
        }
        return;
      }

      try {
        const response = await api.post('/auth/auto-login', { token });

        if (!isMounted) return;

        // Get token and user data from response
        const { token: jwtToken, user } = response.data;

        // Update auth context with token and user data directly
        await login(jwtToken, user);

        toast.success('Login successful! Welcome to EnHomeBuilder.');

        // Check if user needs to set initial password
        if (user.requirePasswordChange) {
          // Redirect to password setup page
          navigate('/workspaces', { replace: true, state: { showPasswordSetup: true } });
        } else {
          // Redirect to workspaces
          navigate('/workspaces', { replace: true });
        }
      } catch (err: any) {
        if (!isMounted) return;

        console.error('Auto-login error:', err);
        const errorMessage = err.response?.data?.message || 'Failed to login. The link may be invalid or expired.';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    performAutoLogin();

    return () => {
      isMounted = false;
    };
  }, [searchParams, navigate, login]);

  if (isLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">
            <h1 className="auth-title">EnHomeBuilder</h1>
          </div>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner"></div>
            <p style={{ marginTop: '1rem', color: '#666' }}>Logging you in...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">
            <h1 className="auth-title">EnHomeBuilder</h1>
          </div>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{
              backgroundColor: '#fee',
              color: '#c33',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
            <button
              onClick={() => navigate('/login')}
              style={{
                backgroundColor: '#48182F',
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#321020'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#48182F'}
            >
              Go to Login Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AutoLogin;
